import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowRight,
  ClipboardCheck,
  Clock,
  Stethoscope,
} from "@tamagui/lucide-icons";
import { Controller, useForm } from "react-hook-form";
import { YStack } from "tamagui";
import { z } from "zod";

import { ConsentType, WorkflowType } from "@/api/generated/models";
import { useUpdateConsents } from "@/api/mutations/consent";
import {
  Button,
  Checkbox,
  Footer,
  Heading,
  Link,
  Paragraph,
  Text,
  XStack,
  useTheme,
} from "@/components";
import { useUserProgression } from "@/conversation/sidebar/UserProgression/UserProgressionContext";
import { FLOW_NAME } from "./useAIDoctorProgression";

const consentSchema = z.object({
  accepted: z.boolean().refine((val) => val === true, {
    message: "You must agree to continue",
  }),
});

type ConsentFormData = z.infer<typeof consentSchema>;

const WORKFLOW_LABELS: Record<
  WorkflowType,
  { subtitle: string; approval: string }
> = {
  rx_renewal: {
    subtitle: "renewing your prescription",
    approval: "for your prescription",
  },
  diagnosis: {
    subtitle: "getting a diagnosis",
    approval: "for your diagnosis",
  },
  // TODO: comment this in when ready
  //   labs: {
  //     subtitle: "ordering labs",
  //     approval: "for your lab order",
  //   },
};

export const AIDoctorConsentForm = ({
  onContinue,
}: {
  onContinue: () => void;
}) => {
  const ctx = useUserProgression();
  const workflowType =
    ctx?.progression.flow_name === FLOW_NAME[WorkflowType.diagnosis]
      ? WorkflowType.diagnosis
      : WorkflowType.rx_renewal;

  const { mutate: updateConsents, isPending } = useUpdateConsents();
  const theme = useTheme();
  const iconColor = theme.iconBase?.get();

  const labels = WORKFLOW_LABELS[workflowType];

  const { handleSubmit, control } = useForm<ConsentFormData>({
    resolver: zodResolver(consentSchema),
    defaultValues: { accepted: false },
  });

  const handleFormSubmit = handleSubmit(() => {
    updateConsents(
      {
        data: {
          consents: {
            [ConsentType.surescripts_consent]: true,
          },
        },
      },
      { onSuccess: () => onContinue() },
    );
  });

  const infoRows = [
    {
      Icon: ClipboardCheck,
      boldText: "Enter profile information ",
      plainText: "to pull your records",
    },
    {
      Icon: Stethoscope,
      boldText: "Answer a few additional questions ",
      plainText: "about your health",
    },
    {
      Icon: Clock,
      boldText: "Get approval within minutes ",
      plainText: labels.approval,
    },
  ];

  return (
    <YStack gap="$200" flex={1} paddingHorizontal="$200" paddingBottom="$200">
      <YStack gap="$100">
        <Heading size="$xl">What to expect</Heading>
        <Paragraph color="$textSecondary" size="$s">
          You are just a few steps away from {labels.subtitle}!
        </Paragraph>
      </YStack>

      <YStack flex={1} gap="$100" paddingVertical="$100">
        {infoRows.map(({ Icon, boldText, plainText }) => (
          <XStack
            key={boldText}
            alignItems="center"
            gap="$150"
            paddingVertical="$125"
          >
            <Icon
              color={iconColor}
              height={24}
              width={24}
              style={{ flexShrink: 0 }}
            />
            <Text>
              <Text semibold color="$textLink">
                {boldText}
              </Text>
              {plainText}
            </Text>
          </XStack>
        ))}
      </YStack>

      <YStack marginBottom="$200">
        <Controller
          control={control}
          name="accepted"
          render={({ field, fieldState }) => (
            <Checkbox
              id="surescripts-consent"
              checked={field.value}
              onPress={() => field.onChange(!field.value)}
              errorText={fieldState.error?.message}
              label={
                <Checkbox.Label htmlFor="surescripts-consent">
                  I agree to Curai Health pulling relevant medical data via{" "}
                  <Link
                    size="$sm"
                    external
                    semibold
                    href="https://www.surescripts.com/"
                  >
                    Surescripts
                  </Link>{" "}
                  to expedite my care.
                </Checkbox.Label>
              }
            />
          )}
        />
      </YStack>
      <Footer mode="dialog">
        <Button
          mode="primary"
          constrained
          onPress={handleFormSubmit}
          loading={isPending}
          iconAfter={ArrowRight}
          dataTrackingId="ai-doctor-consent-submit-button"
        >
          Confirm & continue
        </Button>
      </Footer>
    </YStack>
  );
};
